import {
  Alert,
  AlertIcon,
  Badge,
  Button,
  Flex,
  Icon,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { AxiosError } from 'axios';
import { useRef } from 'react';
import { FiFile, FiUpload } from 'react-icons/fi';
import { useGetTafAdaptationStatus } from '../../../../services/hooks/TafAdaptation/useGetTafAdaptationStatus';
import { useUploadTafAdaptationDocument } from '../../../../services/hooks/TafAdaptation/useUploadTafAdaptationDocument';

const ACCEPTED_TYPES = ['application/pdf', 'image/png', 'image/jpeg'];
const ACCEPTED_EXTENSIONS = '.pdf,.png,.jpg,.jpeg';

interface TafAdaptationModalProps {
  isOpen: boolean;
  onClose: () => void;
  registrationId: string;
}

const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatSize = (bytes: number): string => {
  if (!bytes) return '0 KB';
  const kb = bytes / 1024;
  return kb < 1024 ? `${kb.toFixed(0)} KB` : `${(kb / 1024).toFixed(1)} MB`;
};

const TafAdaptationModal: React.FC<TafAdaptationModalProps> = ({ isOpen, onClose, registrationId }) => {
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement>(null);
  const { data: statusData, isLoading, isError, refetch } = useGetTafAdaptationStatus(registrationId, isOpen);
  const uploadMutation = useUploadTafAdaptationDocument(registrationId);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (inputRef.current) inputRef.current.value = '';
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast({
        title: 'Formato inválido',
        description: 'Apenas arquivos PDF, PNG e JPG são aceitos.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      await uploadMutation.mutateAsync(file);
      await refetch();
      toast({
        title: 'Arquivo enviado com sucesso!',
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
    } catch (err) {
      const axiosError = err as AxiosError<{ message?: string }>;
      const backendMessage = axiosError?.response?.data?.message;

      if (axiosError?.response?.status === 400) {
        await refetch();
      }

      toast({
        title: 'Erro ao enviar arquivo',
        description: backendMessage || 'Tente novamente.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered scrollBehavior="inside" size="xl">
      <ModalOverlay />
      <ModalContent maxW={['95%', '95%', '600px', '650px']} mx={3}>
        <ModalHeader borderBottom="1px solid" borderColor="gray.200" pb={3} fontSize="15px" textTransform="uppercase">
          {statusData?.display_name || 'Envio do Requerimento de Adaptação do TAF'}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody py={5}>
          {isLoading && (
            <Flex justify="center" align="center" minH="150px">
              <Spinner size="lg" color="blue.500" />
            </Flex>
          )}

          {!isLoading && isError && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              Erro ao carregar as informações. Tente novamente.
            </Alert>
          )}

          {!isLoading && !isError && statusData && !statusData.is_allocated && (
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              Você não está alocado(a) para esta etapa.
            </Alert>
          )}

          {!isLoading && !isError && statusData?.is_allocated && (
            <Stack gap={4}>
              <Alert status={statusData.is_open ? 'info' : 'error'} borderRadius="md" alignItems="flex-start">
                <AlertIcon mt={1} />
                <Stack gap={1}>
                  <Text fontSize="sm" fontWeight="bold">
                    Prazo de envio: {formatDate(statusData.window_start)} até {formatDate(statusData.window_end)}
                  </Text>
                  {!statusData.is_open && (
                    <Text fontSize="sm">Fora do período, não é possível enviar ou substituir o arquivo.</Text>
                  )}
                </Stack>
              </Alert>

              {statusData.attachment ? (
                <Flex
                  align="center"
                  justify="space-between"
                  p={3}
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="md"
                  bg="gray.50"
                  gap={3}
                >
                  <Flex align="center" gap={3} flex={1} minW={0}>
                    <Icon as={FiFile} color="blue.500" boxSize={5} flexShrink={0} />
                    <Stack gap={0} minW={0}>
                      <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>
                        {statusData.attachment.name}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {formatSize(statusData.attachment.size)} · Enviado em:{' '}
                        {formatDate(statusData.attachment.created_at)}
                      </Text>
                    </Stack>
                  </Flex>
                  <Badge colorScheme="green" flexShrink={0}>
                    Enviado
                  </Badge>
                </Flex>
              ) : (
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  Nenhum arquivo enviado ainda.
                </Alert>
              )}

              {statusData.is_open && (
                <Input
                  ref={inputRef}
                  type="file"
                  accept={ACCEPTED_EXTENSIONS}
                  display="none"
                  onChange={handleFileChange}
                />
              )}
            </Stack>
          )}
        </ModalBody>

        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose}>
            Fechar
          </Button>
          {statusData?.is_allocated && statusData.is_open && (
            <Button
              leftIcon={<Icon as={FiUpload} />}
              colorScheme="green"
              isDisabled={!statusData.can_upload || uploadMutation.isPending}
              isLoading={uploadMutation.isPending}
              loadingText="Enviando..."
              onClick={() => inputRef.current?.click()}
            >
              {statusData.attachment ? 'Substituir arquivo' : 'Enviar arquivo'}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default TafAdaptationModal;
